<!-- eslint-disable no-debugger -->
<template>
  <div class="form-card-list">
    <!-- Cube Scroll 实现下拉刷新和上拉加载 -->
    <cube-scroll
      ref="scroll"
      :data="cardData"
      :options="scrollOptions"
      @pulling-down="onPullingDown"
      @pulling-up="onPullingUp"
    >
      <!-- 卡片列表 -->
      <div class="card-list-wrapper">
        <div
          v-for="(item, index) in cardData"
          :key="getRowKey(item, index)"
          class="card-item"
          @click="handleCardClick(item, index)"
        >
          <!-- 卡片内容插槽 - 父组件自定义 -->
          <slot name="card" :item="item" :index="index">
            <!-- 默认卡片样式 -->
            <div class="default-card">
              <div class="card-content">
                <p v-for="(value, key) in item" :key="key">
                  <span class="label">{{ key }}:</span>
                  <span class="value">{{ value }}</span>
                </p>
              </div>
            </div>
          </slot>
        </div>

        <!-- 空状态 -->
        <div v-if="!loading && cardData.length === 0" class="empty-state">
          <van-empty description="暂无数据" />
        </div>

        <!-- 加载中状态 -->
        <div v-if="loading && cardData.length === 0" class="loading-state">
          <van-loading size="24px"><span style="font-size: 16px;">加载中...</span></van-loading>
        </div>
      </div>

      <!-- 下拉刷新提示 -->
      <template #pulldown="props">
        <div v-if="props.pullDownRefresh" class="cube-pulldown-wrapper">
          <div v-show="props.beforePullDown" class="before-trigger">
            <span>{{ props.bubbleY > 50 ? '释放刷新' : '下拉刷新' }}</span>
          </div>
          <div v-show="!props.beforePullDown" class="after-trigger">
            <div v-show="props.isPullingDown" class="loading">
              <van-loading size="20px" />
            </div>
            <div v-show="!props.isPullingDown" class="text">
              <span>刷新成功</span>
            </div>
          </div>
        </div>
      </template>

      <!-- 上拉加载提示 -->
      <template #pullup="props">
        <div v-if="props.pullUpLoad" class="cube-pullup-wrapper">
          <div v-show="props.isPullingUp" class="loading">
            <van-loading size="20px">加载中...</van-loading>
          </div>
          <div v-show="!props.isPullingUp && hasMore" class="text">
            <van-loading size="24px"><span style="font-size: 16px;">上拉加载更多</span></van-loading>

          </div>
          <div v-show="!hasMore" class="text no-more">
            <span>没有更多了</span>
          </div>
        </div>
      </template>
    </cube-scroll>
  </div>
</template>

<script>
import { getData, queryMeta } from '@/api/tjls/index'
import { get, isEmpty, map, filter, isFunction } from 'lodash'
import ValueFormatter from '@/utils/valueFormatter'

export default {
  name: 'FormCardList',
  props: {
    // 查询字段
    select: {
      type: Array,
      default: () => []
    },
    // 排序规则
    order: {
      type: Array,
      default: () => []
    },
    // 表单key
    formKey: {
      type: String,
      default: ''
    },
    // 行key
    rowKey: {
      type: String,
      default: ''
    },
    // 标题
    title: {
      type: String,
      default: ''
    },
    // 自定义顶部搜索
    customTopSearch: {
      type: Object,
      default: () => ({})
    },
    // 特殊字段数组
    specialFieldArr: {
      type: Array,
      default: () => []
    },
    // 搜索配置
    searchConfig: {
      type: Object,
      default: () => ({})
    },
    // 每页数量
    pageSize: {
      type: Number,
      default: 20
    },
    // 卡片点击事件
    cardClick: {
      type: Function,
      default: null
    },
    // 筛选字段白名单，空数组表示显示全部
    filterFields: {
      type: Array,
      default: () => []
    },
    // 手动配置模式
    manualMode: {
      type: Boolean,
      default: false
    },
    // 手动配置的筛选字段定义
    manualFilters: {
      type: Array,
      default: () => []
    },
    // 手动配置的列字段定义（用于格式化）
    manualColumns: {
      type: Object,
      default: () => ({})
    },
    // 自定义数据获取函数
    customDataFetch: {
      type: Function,
      default: null
    },
    // 详情页缓存名称（用于页面缓存管理）
    detailPageCacheName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      formModel: {},
      loading: false,
      cardData: [],
      filters: [],
      fieldMeta: {}, // 字段元信息 { fieldName: { format, label, ... } }
      paginationData: {
        count: 0,
        pageNum: 1,
        pageSize: this.pageSize
      },
      hasMore: true,
      savedScrollY: 0,
      scrollOptions: {
        pullDownRefresh: {
          threshold: 60,
          stop: 40,
          txt: '刷新成功'
        },
        pullUpLoad: {
          threshold: 10,
          txt: {
            more: '加载更多',
            noMore: '没有更多了'
          }
        },
        scrollbar: false,
        bounce: {
          top: true,
          bottom: true
        }
      }
    }
  },
  computed: {
    customTopSearchKey() {
      return get(this.customTopSearch, 'filedKey')
    },
    isCustomTopSearch() {
      return !isEmpty(this.customTopSearch)
    }
  },
  mounted() {
    if (this.isCustomTopSearch) {
      this.formModel[this.customTopSearchKey] = undefined
    }
    this.initMeta()
  },
  activated() {
    this.restoreScroll()
  },
  deactivated() {
    this.saveScroll()
  },
  methods: {
    // 初始化元数据
    async initMeta() {
      // 如果是手动配置模式，使用手动配置的元数据
      if (this.manualMode) {
        this.filters = this.filterFields.length > 0
          ? this.manualFilters.filter(item => this.filterFields.includes(item.name))
          : this.manualFilters
        this.fieldMeta = this.manualColumns

        this.$emit('meta-loaded', {
          filters: this.filters,
          columns: this.fieldMeta,
          searchList: filter(this.filters, item => item.name !== this.customTopSearchKey)
            .sort((a, b) => a.index - b.index)
        })
        return
      }

      // 否则使用 API 获取元数据
      try {
        const res = await queryMeta(this.formKey)
        const allFilters = Object.values(res.data.filters)
        // 根据 filterFields 白名单过滤，空数组表示全部
        this.filters = this.filterFields.length > 0
          ? allFilters.filter(item => this.filterFields.includes(item.name))
          : allFilters
        // 保存字段元信息
        this.fieldMeta = res.data.columns || {}
        // 暴露 searchList 给父组件用于 SearchBox
        this.$emit('meta-loaded', {
          filters: this.filters,
          columns: this.fieldMeta,
          searchList: filter(this.filters, item => item.name !== this.customTopSearchKey)
            .sort((a, b) => a.index - b.index)
        })
      } catch (error) {
        console.error('获取元数据失败:', error)
      }
    },

    // 获取数据
    async getData(isSearch = false) {
      if (isSearch) {
        this.paginationData.pageNum = 1
        this.cardData = []
        this.hasMore = true
      }

      this.loading = true

      try {
        let res
        // 如果提供了自定义数据获取函数，使用它
        if (this.customDataFetch) {
          res = await this.customDataFetch({
            pageNum: this.paginationData.pageNum,
            pageSize: this.paginationData.pageSize,
            formModel: this.formModel
          })
        } else {
          // 否则使用默认的数据获取逻辑
          const params = {
            select: this.select,
            limit: this.paginationData.pageSize,
            offset: (this.paginationData.pageNum - 1) * this.paginationData.pageSize,
            order: this.order
          }

          const where = map(this.filters, item => {
            const value = get(this.formModel, item.name)
            if (!isEmpty(value)) {
              let valueItem = get(this.formModel, item.name)
              if (!Array.isArray(valueItem) && item.matchType === 'IN') {
                valueItem = [valueItem]
              }
              if (this.specialFieldArr.includes(item.name)) {
                return {
                  op: '>',
                  value: 0,
                  function: {
                    name: 'ARRAY_SIZE',
                    nestedFunction: {
                      name: 'ARRAY_INTERSECT',
                      args: [item.columnName, `ARRAY('${valueItem}')`]
                    }
                  }
                }
              } else {
                return {
                  field: item.columnName,
                  value: valueItem,
                  op: item.matchType
                }
              }
            }
            return undefined
          })

          params.where = filter(where, item => !isEmpty(item))
          const apiRes = await getData(this.formKey, params)
          res = {
            items: apiRes.data.items || [],
            totalCount: apiRes.data.totalCount
          }
        }

        const newData = res.items || []

        if (isSearch) {
          this.cardData = newData
        } else {
          this.cardData = [...this.cardData, ...newData]
        }

        this.paginationData.count = res.totalCount

        // 判断是否还有更多数据
        this.hasMore = this.cardData.length < this.paginationData.count

        this.loading = false
        return true
      } catch (error) {
        console.error('获取数据失败:', error)
        this.loading = false
        return false
      }
    },

    // 下拉刷新
    async onPullingDown() {
      this.paginationData.pageNum = 1
      await this.getData(true)
      this.$refs.scroll.forceUpdate()
    },

    // 上拉加载
    async onPullingUp() {
      if (!this.hasMore) {
        this.$refs.scroll.forceUpdate()
        return
      }
      this.paginationData.pageNum++
      await this.getData(false)
      this.$refs.scroll.forceUpdate()
    },

    // 搜索
    search(data) {
      this.formModel = {
        ...this.formModel,
        ...data
      }
      this.scrollToTop()
      this.getData(true)
    },

    // 重置
    reset() {
      if (this.isCustomTopSearch) {
        this.formModel = {
          [this.customTopSearchKey]: undefined
        }
      } else {
        this.formModel = {}
      }
      this.scrollToTop()
      this.getData(true)
    },

    // 滚动到顶部
    scrollToTop() {
      if (this.$refs.scroll && this.$refs.scroll.scroll) {
        this.$refs.scroll.scrollTo(0, 0, 300)
      }
      this.savedScrollY = 0
    },

    saveScroll() {
      if (this.$refs.scroll && this.$refs.scroll.scroll) {
        this.savedScrollY = this.$refs.scroll.scroll.y
      }
    },

    restoreScroll() {
      this.$nextTick(() => {
        if (this.$refs.scroll && this.$refs.scroll.scroll) {
          this.$refs.scroll.scrollTo(0, this.savedScrollY, 0)
        }
      })
    },

    // 获取行key
    getRowKey(item, index) {
      if (isFunction(this.rowKey)) {
        return this.rowKey(item)
      }
      return this.rowKey ? item[this.rowKey] : index
    },

    // 卡片点击
    handleCardClick(item, index) {
      this.saveScroll()

      if (this.cardClick) {
        this.cardClick(item, index)
      } else {
        this.$emit('card-click', item, index)
      }
    },

    // 刷新列表
    refresh() {
      this.getData(true)
    },

    // 格式化字段值
    formatValue(fieldName, value) {
      const fieldConfig = this.fieldMeta[fieldName]
      const format = fieldConfig.format
      return ValueFormatter.format(ValueFormatter.numbered(value), format)
    }
  }
}
</script>

<style lang="stylus" scoped>
.form-card-list
  height 100%
  overflow hidden

.card-list-wrapper
  padding 20px

.card-item
  margin-bottom 20px
  &:last-child
    margin-bottom 0

.default-card
  background #fff
  border-radius 16px
  padding 24px
  box-shadow 0 2px 8px rgba(0, 0, 0, 0.08)
  .card-content
    p
      margin 0 0 12px 0
      line-height 1.5
      &:last-child
        margin-bottom 0
      .label
        font-size 24px
        color #999
        margin-right 8px
      .value
        font-size 26px
        color #333

.empty-state,
.loading-state
  padding 100px 20px
  text-align center

.empty-state
  ::v-deep .van-empty__description
    font-size 28px
    color #64748b
    line-height 1.5

.cube-pulldown-wrapper,
.cube-pullup-wrapper
  padding 16px
  text-align center
  font-size 14px
  color #999
  .loading
    display flex
    justify-content center
    align-items center
  .text
    &.no-more
      color #ccc
</style>
